import React, { useCallback, useState } from "react";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import classNames from "classnames";
import { UploadIcon } from "@icons";
import { StandardModal } from "@design-system/Modal/Modal";
import { Menu } from "@design-system/Menu/Menu.lib";
import { HStack } from "@design-system/HStack/HStack";
import { useFinancialDocumentUpload } from "@components/documents/FinancialDocumentUpload/useFinancialDocumentUpload";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import { DocumentSelect } from "@components/documents/FinancialDocumentList/DocumentSelect";
import { DocumentSelectVariant } from "@components/documents/FinancialDocumentSelect/financialDocumentSelectViewConfig";
import { useFinancialDocumentFilters } from "@components/documents/useFinancialDocumentFilters";
import styles from "./AddDocument.module.scss";

interface Props {
    attachedDocumentIds: number[];
    attachDocument: (documentId: number) => void;
    attachDocuments?: (documentIds: number[]) => void;
    closeBrowseModalOnAttach?: boolean;
    trigger?: React.ReactElement;
    variant?: DocumentSelectVariant;
    initialFilters?: Parameters<typeof useFinancialDocumentFilters>[1];
}

export const AddDocument: React.FC<Props> = ({
    attachDocument,
    attachDocuments,
    attachedDocumentIds,
    closeBrowseModalOnAttach = false,
    trigger,
    variant,
    initialFilters,
}) => {
    const [showDocumentModal, setShowDocumentModal] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const { inputProps, open } = useFinancialDocumentUpload();
    const { refuseIfViewOnly } = useViewOnlyRefusal();

    const handleAttachDocument = useCallback(
        (documentId: number) => {
            if (refuseIfViewOnly()) {
                return;
            }
            attachDocument(documentId);
            if (closeBrowseModalOnAttach) {
                setShowDocumentModal(false);
            }
        },
        [attachDocument, closeBrowseModalOnAttach, refuseIfViewOnly],
    );

    const handleAttachDocuments = useCallback(
        (documentIds: number[]) => {
            if (refuseIfViewOnly()) {
                return;
            }
            attachDocuments?.(documentIds);
            if (closeBrowseModalOnAttach) {
                setShowDocumentModal(false);
            }
        },
        [attachDocuments, closeBrowseModalOnAttach, refuseIfViewOnly],
    );

    return (
        <>
            <Tooltip
                content="Drop any files here or click to upload"
                disabled={isDropdownOpen}
            >
                <div>
                    <div
                        className={classNames(styles.addDocument, {
                            [styles.hasDocumentsAttached]:
                                attachedDocumentIds.length > 0,
                            [styles.defaultTrigger]: !trigger,
                        })}
                    >
                        <input {...inputProps} />

                        <Menu.Root
                            onOpenChange={setIsDropdownOpen}
                            trigger={
                                trigger ?? (
                                    <button
                                        type="button"
                                        data-add-document-trigger
                                        className={classNames(
                                            "mb-0",
                                            styles.uploadControl,
                                            styles.uploadDocument,
                                        )}
                                    >
                                        <HStack
                                            as="span"
                                            justify="between"
                                            align="center"
                                            className="w-100"
                                        >
                                            <span>Upload document</span>
                                            <span>
                                                <UploadIcon />
                                            </span>
                                        </HStack>
                                    </button>
                                )
                            }
                        >
                            <Menu.Item onClick={open}>
                                Upload Document
                            </Menu.Item>
                            <Menu.Item
                                onClick={() => setShowDocumentModal(true)}
                            >
                                Browse from documents
                            </Menu.Item>
                        </Menu.Root>
                    </div>
                </div>
            </Tooltip>

            <StandardModal
                className="fullscreen-modal"
                show={showDocumentModal}
                onHide={() => setShowDocumentModal(false)}
            >
                <DocumentSelect
                    attachedDocumentIds={attachedDocumentIds}
                    attachDocument={handleAttachDocument}
                    attachDocuments={
                        attachDocuments ? handleAttachDocuments : undefined
                    }
                    variant={variant}
                    initialFilters={initialFilters}
                />
            </StandardModal>
        </>
    );
};
